import { apiFetch } from '@/api/client';
import type { AnimatedImage } from '@/lib/edl-types';
import type { OutputOptions } from '@/lib/output-options';

// Typed calls for the ENG-280 job registry API. Shapes mirror
// apps/edl-api/jobs/lib/jobs.js toApi().

export type JobStatus = 'queued' | 'in-progress' | 'completed' | 'failed' | 'killed';
export type JobStage =
  | 'in-edl-generation'
  | 'in-edl-editor'
  | 'in-edl-processor'
  | 'in-fireside-publishing'
  | null;

export type ContentType = 'podcast' | 'musicvideo';
// Where the heavy stages run (ENG-352): AWS Batch, or a Mac running
// reframer-worker with Batch as the fallback. The API also takes a tenant
// default when the job names neither.
export type Compute = 'batch' | 'local';
// The transcription engine: AWS Transcribe, or WhisperX on the worker (only
// honoured when the job's compute is local).
export type Transcriber = 'aws' | 'local';

export interface JobRequest {
  s3Source: string;
  contentType: ContentType;
  edlRules?: string;
  useSpeakerData: boolean;
  // shot-features capability switches, always sent explicitly so the CLI
  // invocation never depends on a profile default.
  detectGraphicOverlays: boolean;
  detectVideoRegions: boolean;
  edlEditStepRequired: boolean;
  s3Destination?: string;
  firesideEpisodeSlug?: string;
  webhookUrl?: string;
  compute?: Compute;
  transcriber?: Transcriber;
  // The renders' output options (ENG-195); set on every job created since.
  outputOptions?: OutputOptions;
}

export interface JobEditorContext {
  videoURL: string;
  edlURL: string;
  s3Bucket: string;
  s3KeyPrefix: string;
}

export interface JobSourceMedia {
  sizeBytes?: number;
  durationSeconds?: number;
  width?: number;
  height?: number;
  frameRate?: number;
}

// The job's animated webP poster (ENG-320): one per job, replaced on every
// regeneration. url (to show it) and downloadUrl (to save it) are presigned
// for an hour and change on every fetch, so images reload on updatedAt, not
// on url.
export interface JobPoster {
  key: string;
  // 'render': restored from a render's own poster by Re-Edit (ENG-345).
  source: 'initial' | 'composed' | 'editor' | 'render';
  updatedAt: string;
  url?: string;
  downloadUrl?: string;
}

// One file a render produced (ENG-345): its output type and name. A
// package (HLS) is shown by its master playlist and lists every file in
// it, for a zipped download.
export interface RenderFile {
  type: string;
  name: string;
  package?: string[];
}

// One entry of the job's render history (ENG-345), oldest first.
export interface JobRender {
  n: number;
  // s3:// prefix of the render's folder in the media bucket.
  prefix: string;
  createdAt: string;
  outputOptions?: OutputOptions;
  baseFilename?: string;
  files: RenderFile[];
  // The metadata files snapshotted with the render (what Re-Edit restores).
  snapshot: string[];
  // The render's own poster, with a presigned url (an hour) from GET.
  poster?: { key: string; url?: string };
}

export interface Job {
  jobId: string;
  tenantId: string;
  status: JobStatus;
  currentStage: JobStage;
  percentComplete: number;
  sourceName: string;
  sourceMedia?: JobSourceMedia;
  request: JobRequest;
  // GET /v1/jobs/{jobId} only: presigned (one hour) download of the
  // original source under its own name, when the API can sign it.
  sourceDownloadUrl?: string;
  editor?: JobEditorContext;
  outputs?: {
    renderPrefix?: string;
    // The latest render's output options (renders since ENG-195).
    renderOutputOptions?: OutputOptions;
    // s3Destination copy
    destination?: string;
    posterDestination?: string;
    // Fireside CMS publish
    hlsURI?: string;
    dashURI?: string;
    masterFileURI?: string;
    publishVersion?: number;
    episodeId?: string;
    episodeCreated?: boolean;
    captions?: 'reused-source-transcript' | 'transcribed' | 'none';
    studioURL?: string;
  };
  renderCount: number;
  // Every render so far (ENG-345); jobs rendered before render history
  // have none and only outputs.renderPrefix.
  renders?: JobRender[];
  publishStatus?: string;
  lastWebhook?: {
    deliveryId: string;
    event: string;
    url: string;
    attempt: number;
    httpStatus: number;
    ok: boolean;
    // Set by newer deliveries: false while SQS will retry, true once the
    // attempt settled the delivery (2xx, or a 4xx that is not retried).
    final?: boolean;
    at: string;
    detail?: string;
  };
  edlEditorURL?: string;
  poster?: JobPoster;
  error?: { name: string; cause: string };
  // Free-text notes from the job page (ENG-332); absent until saved.
  jobNotes?: string;
  createdAt: string;
  updatedAt: string;
  finishedAt?: string;
}

export interface CreateJobInput {
  s3Source: string;
  contentType: ContentType;
  edlRules?: string;
  useSpeakerData?: boolean;
  detectGraphicOverlays?: boolean;
  detectVideoRegions?: boolean;
  edlEditStepRequired?: boolean;
  s3Destination?: string;
  firesideEpisodeSlug?: string;
  webhookUrl?: string;
  outputOptions?: OutputOptions;
  compute?: Compute;
  transcriber?: Transcriber;
}

export interface TenantSummary {
  tenantId: string;
  name?: string;
  enabled: boolean;
}

// Content types as the API knows them (the pipeline maps 'musicvideo' to
// its own 'music' vocabulary). Labels per ENG-291.
export const CONTENT_TYPE_OPTIONS: { value: ContentType; label: string }[] = [
  { value: 'podcast', label: 'Chatcast (podcasts, news, webinars)' },
  { value: 'musicvideo', label: 'Music (performances, music videos)' },
];

export function contentTypeLabel(value: ContentType | string): string {
  return CONTENT_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

// The short form for lists: the label up to its parenthetical.
export function contentTypeShortLabel(value: ContentType | string): string {
  return contentTypeLabel(value).replace(/\s*\(.*$/, '');
}

// Rule-preset schema filenames the API accepts — mirror of the server's
// VALID_EDL_RULES (apps/edl-api/jobs/lib/jobs.js) with display labels.
export const DEFAULT_EDL_RULES: Record<ContentType, string> = {
  podcast: 'edl_rules_podcast_standard.json',
  musicvideo: 'edl_rules_stage_performance.json',
};

// The New Job form's Compute select. The form always names the compute
// (the tenant default is for API clients that leave it out); the form
// remembers the last choice per browser.
export const COMPUTE_OPTIONS: { value: Compute; label: string }[] = [
  { value: 'batch', label: 'AWS Batch' },
  { value: 'local', label: 'Local Worker' },
];
export const DEFAULT_COMPUTE: Compute = 'batch';
const COMPUTE_STORAGE_KEY = 'edl-tools.compute';

/** The browser's remembered Compute choice, or the default. */
export function rememberedCompute(): Compute {
  try {
    const stored = window.localStorage.getItem(COMPUTE_STORAGE_KEY);
    if (COMPUTE_OPTIONS.some((o) => o.value === stored)) return stored as Compute;
  } catch {
    // storage unavailable (private mode etc.)
  }
  return DEFAULT_COMPUTE;
}

export function rememberCompute(value: Compute): void {
  try {
    window.localStorage.setItem(COMPUTE_STORAGE_KEY, value);
  } catch {
    // best-effort persistence only
  }
}
export const TRANSCRIBER_OPTIONS: { value: Transcriber; label: string }[] = [
  { value: 'aws', label: 'AWS Transcribe' },
  { value: 'local', label: 'Local (WhisperX + pyannote)' },
];

export function computeLabel(value: Compute | string): string {
  return COMPUTE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export function transcriberLabel(value: Transcriber | string): string {
  return TRANSCRIBER_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** Whether the Transcriber select applies: the speaker-data box decides
 * whether a job transcribes at all, and only a job on a local worker can
 * transcribe locally (a Batch job always uses AWS Transcribe). */
export function transcriberApplies(compute: Compute, useSpeakerData: boolean): boolean {
  return useSpeakerData && compute === 'local';
}

/** The compute fields of a create-job body from the form's choices: the
 * compute always; the transcriber only when the select applies and it is
 * local. */
export function computeFields(
  compute: Compute,
  transcriber: Transcriber,
  useSpeakerData: boolean,
): Pick<CreateJobInput, 'compute' | 'transcriber'> {
  const fields: Pick<CreateJobInput, 'compute' | 'transcriber'> = { compute };
  if (transcriberApplies(compute, useSpeakerData) && transcriber !== 'aws') fields.transcriber = transcriber;
  return fields;
}

export const EDL_RULES_OPTIONS: { value: string; label: string }[] = [
  { value: 'edl_rules_default.json', label: 'Default' },
  { value: 'edl_rules_podcast_standard.json', label: 'Podcast Standard' },
  { value: 'edl_rules_podcast_bradybunch.json', label: 'Podcast Brady Bunch' },
  { value: 'edl_rules_podcast_hardcoded_positioning.json', label: 'Podcast Hardcoded Positioning' },
  { value: 'edl_rules_music_videos.json', label: 'Music Videos' },
  { value: 'edl_rules_stage_performance.json', label: 'Stage Performance' },
  { value: 'edl_rules_tinydesk.json', label: 'Tiny Desk' },
];

export function listJobs(params: { status?: string; cursor?: string } = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.cursor) search.set('cursor', params.cursor);
  const qs = search.toString();
  return apiFetch<{ jobs: Job[]; nextCursor?: string }>(`/v1/jobs${qs ? `?${qs}` : ''}`);
}

export function getJob(jobId: string) {
  return apiFetch<Job>(`/v1/jobs/${encodeURIComponent(jobId)}`);
}

export function createJob(input: CreateJobInput) {
  return apiFetch<{ status: string; jobId: string; tenantId: string }>('/v1/jobs', {
    method: 'POST',
    body: input,
  });
}

// action 'render' renders and returns the job to the editor stage (repeat
// as often as needed); 'finish' closes the edit/render loop.
export function processJob(
  jobId: string,
  options: { action: 'render' | 'finish'; edlModified?: boolean; outputOptions?: OutputOptions }
) {
  return apiFetch<{ status: string; jobId: string }>(
    `/v1/jobs/${encodeURIComponent(jobId)}/process`,
    { method: 'POST', body: options }
  );
}

// Stores a changed poster (the EDL's animated_image block, nothing else)
// in the job's saved EDL and re-renders the poster from it (synchronous,
// seconds). Called by the Edit Poster Image panel's Save.
export function updatePoster(jobId: string, animatedImage: AnimatedImage) {
  return apiFetch<{ status: string; jobId: string; poster: JobPoster }>(
    `/v1/jobs/${encodeURIComponent(jobId)}/poster`,
    { method: 'POST', body: { animatedImage } }
  );
}

// Saves the job page's notes (an empty string clears them) and returns the
// job as getJob() does.
export function updateJobNotes(jobId: string, jobNotes: string) {
  return apiFetch<Job>(`/v1/jobs/${encodeURIComponent(jobId)}`, {
    method: 'PATCH',
    body: { jobNotes },
  });
}

// Stops the orchestrator, its running child and any Batch job; the row
// lands in `killed`. Idempotent on an already-killed job; 409 on
// completed/failed.
export function killJob(jobId: string) {
  return apiFetch<{ status: string; job: Job }>(
    `/v1/jobs/${encodeURIComponent(jobId)}/kill`,
    { method: 'POST', body: {} }
  );
}

// Confirmation wording per ENG-278, shared by the detail page and the
// list's group delete.
export const DELETE_JOB_CONFIRM = 'Are you sure? Files will be permanently deleted.';
// Added when the job (or one of the selected jobs) is still running.
export const DELETE_LIVE_JOB_NOTE = 'It is still running and will be killed first.';

// Removes a job and its files (work, published and render prefixes, a
// browser-uploaded source, review notes, the row). A running job is
// killed first. `kept` lists what the API left alone (s3Destination
// copies, a CMS publish).
export function deleteJob(jobId: string) {
  return apiFetch<{
    status: string;
    jobId: string;
    tenantId: string;
    deleted: { objects: number; notes: number };
    kept: string[];
  }>(`/v1/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' });
}

// Re-Edit (ENG-345): render n's snapshotted metadata replaces the job's
// working copies, its poster becomes the job poster, and a completed job
// is reopened at the editor stage. Returns the job as getJob() does.
export function reeditRender(jobId: string, n: number) {
  return apiFetch<{ status: string; jobId: string; render: number; restored: string[]; removed: string[]; job: Job }>(
    `/v1/jobs/${encodeURIComponent(jobId)}/renders/${n}/reedit`,
    { method: 'POST', body: {} }
  );
}

// A render's HLS/DASH package as a zip, built on the server the first time
// it is asked for (ENG-366). 'ready' carries a presigned download url;
// 'zipping' means poll again (the same call) until it is ready.
export interface RenderZipStatus {
  status: 'ready' | 'zipping';
  url?: string;
  filename?: string;
}

export function requestRenderZip(jobId: string, n: number, type: string) {
  return apiFetch<RenderZipStatus>(
    `/v1/jobs/${encodeURIComponent(jobId)}/renders/${n}/zip`,
    { method: 'POST', body: { type } }
  );
}

export function reopenJob(jobId: string) {
  return apiFetch<{ status: string; jobId: string; tenantId: string }>(
    `/v1/jobs/${encodeURIComponent(jobId)}/reopen`,
    { method: 'POST', body: {} }
  );
}

export function listTenants() {
  return apiFetch<{ tenants: TenantSummary[] }>('/v1/tenants');
}

// ULIDs are 26 chars of Crockford base32 — how EditorPage tells a registry
// job id apart from the legacy "jobId = s3KeyPrefix" convention.
export function isJobId(value: string | undefined): value is string {
  return !!value && /^[0-9A-HJKMNP-TV-Z]{26}$/.test(value);
}

export function isTerminal(status: JobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'killed';
}
